import type { MaybeRef, Reactive } from "vue"
import type { MetaV1, ServerResponse, ServerResponseV1 } from "@/http"
import type { IGuestResponse, IGuestResponseV1, RsvpStatus } from "@/models"
import { useQuery } from "@tanstack/vue-query"
import { ref, toValue } from "vue"
import { axios, v1 } from "@/axios"
import { Guest } from "@/models"

interface IListGuestFilters {
  page?: number
  perPage?: number
  search?: string
  rsvpStatus?: RsvpStatus[]
  showDietaryRequirements?: boolean
}

interface IMetaExtentions {
  totalAcceptedCount?: number
  totalDeclinedCount?: number
  totalMaybeCount?: number
  acceptedGuestCount?: number
  declinedGuestCount?: number
  maybeGuestCount?: number
}

export async function listGuests(eventVaultId: number, filters: Reactive<IListGuestFilters> = {}) {
  const query = new URLSearchParams()

  if (filters.page)
    query.append("page", filters.page.toString())
  if (filters.perPage)
    query.append("perPage", filters.perPage.toString())
  if (filters.search)
    query.append("search", filters.search)

  const endpoint = `api/event/${eventVaultId}/guests`
  const response = await axios.get<ServerResponse<IGuestResponse[]>>(endpoint, { params: query })

  return {
    guests: response.data.Data?.map(guest => Guest.fromResponse(guest)) ?? [],
    links: response.data.Links,
  }
}

export async function listGuestsV1(eventVaultId: number, filters: Reactive<IListGuestFilters> = {}) {
  const query = new URLSearchParams()

  if (filters.page)
    query.append("page[number]", filters.page.toString())
  if (filters.perPage)
    query.append("page[size]", filters.perPage.toString())
  if (filters.search)
    query.append("search", filters.search)
  if (filters.rsvpStatus && filters.rsvpStatus.length > 0) {
    filters.rsvpStatus.forEach((status) => {
      query.append("rsvpStatus", status)
    })
  }
  if (filters.showDietaryRequirements !== undefined) {
    query.append("showDietaryRequirements", filters.showDietaryRequirements.toString())
  }

  const endpoint = `/events/${eventVaultId}/guests`
  const response = await v1.get<ServerResponseV1<IGuestResponseV1[], MetaV1<IMetaExtentions>>>(endpoint, { params: query })

  return {
    guests: response.data.data?.map(guest => Guest.fromResponseV1(guest)) ?? [],
    links: response.data.links,
    meta: response.data.meta,
  }
}

export function useListGuests(eventVaultId?: MaybeRef<number | undefined>, filters: Reactive<IListGuestFilters> = {}) {
  const pageCount = ref(0)

  const query = useQuery({
    queryKey: ["listGuests", eventVaultId, filters],
    queryFn: async () => {
      const response = await listGuests(toValue(eventVaultId)!, filters)

      if (response.links?.Last) {
        pageCount.value = Number(response.links.Last)
      }

      return response.guests
    },
    enabled: !!toValue(eventVaultId),
  })

  return { ...query, pageCount }
}

export function useListGuestsV1(eventVaultId?: MaybeRef<number | undefined>, filters: Reactive<IListGuestFilters> = {}) {
  const pageCount = ref(0)

  const query = useQuery({
    queryKey: ["listGuestsV1", eventVaultId, filters],
    queryFn: async () => {
      const response = await listGuestsV1(toValue(eventVaultId)!, filters)

      if (response.meta?.totalPages) {
        pageCount.value = response.meta.totalPages
      }

      return response
    },
    enabled: !!toValue(eventVaultId),
  })

  return { ...query, pageCount }
}
